// src/charts/heating/PowerCurrentChart_V2.js

import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import { InfoBox } from '../../Components';
import {
  extractTimeseries,
  aggregateToHourly,
  getHourlyTicks,
  formatHourLabel,
  hasMetering,
  isMobileLayout
} from './heatingChartUtils';
import { t } from '../../i18n';

/**
 * PowerCurrentChart_V2 - Displays current (A) over time with fuse limit reference line
 *
 * Two modes:
 * 1. Metered data (with phase currents L1, L2, L3): Shows individual phase currents with reference line
 * 2. Habitaio controlled only: Shows estimated phase current with reference line
 *
 * Reference line is dynamically set from metadata.current_stats.fuse_max
 */
const PowerCurrentChart_V2 = ({ data, metadata }) => {
  const chartRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [showDetails, setShowDetails] = useState(false); // Mobile: Show 15-min details

  // Extract timeseries
  const timeseries = useMemo(() => extractTimeseries(data), [data]);

  // Check if metering is available
  const hasMeteredData = useMemo(() => hasMetering(data), [data]);

  // Get fuse_max from metadata (if available)
  const fuseMax = useMemo(() => {
    const value = metadata?.current_stats?.fuse_max || null;
    console.log('PowerCurrentChart_V2: fuse_max =', value, 'from metadata.current_stats:', metadata?.current_stats);
    return value;
  }, [metadata]);

  // Determine if mobile layout
  const isMobile = useMemo(() => isMobileLayout(containerWidth), [containerWidth]);

  // Process data for chart (15-min or hourly based on mobile + toggle)
  const processedData = useMemo(() => {
    const shouldAggregate = isMobile && !showDetails;
    const source = shouldAggregate ? aggregateToHourly(timeseries) : timeseries;

    return source.map(snapshot => {
      if (hasMeteredData && snapshot.metered?.current) {
        // Metered data available - show phase currents
        return {
          timestamp: snapshot.timestamp,
          phase1: snapshot.metered.current.phase1 || 0,
          phase2: snapshot.metered.current.phase2 || 0,
          phase3: snapshot.metered.current.phase3 || 0,
          totalCurrent: snapshot.metered.current.total || 0
        };
      } else {
        // No metered data - show estimated phase current (total / 3)
        const totalCurrent = snapshot.consumption?.current || 0;
        const estimatedPhaseCurrent = totalCurrent / 3;
        return {
          timestamp: snapshot.timestamp,
          estimatedPhaseCurrent: estimatedPhaseCurrent,
          totalCurrent: totalCurrent
        };
      }
    });
  }, [timeseries, isMobile, showDetails, hasMeteredData]);

  // Get tick marks (hourly)
  const hourlyTicks = useMemo(() => {
    if (isMobile && !showDetails) {
      // Show fewer ticks on mobile hourly view
      return processedData
        .filter((_, index) => index % 2 === 0) // Every 2 hours
        .map(d => d.timestamp);
    }
    return getHourlyTicks(processedData);
  }, [processedData, isMobile, showDetails]);

  // Calculate statistics for InfoBox
  const currentStats = useMemo(() => {
    if (processedData.length === 0) return null;

    if (hasMeteredData) {
      const phase1Values = processedData.map(d => d.phase1).filter(v => v > 0);
      const phase2Values = processedData.map(d => d.phase2).filter(v => v > 0);
      const phase3Values = processedData.map(d => d.phase3).filter(v => v > 0);

      return {
        phase1Max: phase1Values.length > 0 ? Math.max(...phase1Values) : 0,
        phase2Max: phase2Values.length > 0 ? Math.max(...phase2Values) : 0,
        phase3Max: phase3Values.length > 0 ? Math.max(...phase3Values) : 0,
        phase1Avg: phase1Values.length > 0 ? phase1Values.reduce((a, b) => a + b, 0) / phase1Values.length : 0,
        phase2Avg: phase2Values.length > 0 ? phase2Values.reduce((a, b) => a + b, 0) / phase2Values.length : 0,
        phase3Avg: phase3Values.length > 0 ? phase3Values.reduce((a, b) => a + b, 0) / phase3Values.length : 0
      };
    } else {
      const estimatedPhaseValues = processedData.map(d => d.estimatedPhaseCurrent).filter(v => v > 0);
      return {
        estimatedPhaseMax: estimatedPhaseValues.length > 0 ? Math.max(...estimatedPhaseValues) : 0,
        estimatedPhaseAvg: estimatedPhaseValues.length > 0 ? estimatedPhaseValues.reduce((a, b) => a + b, 0) / estimatedPhaseValues.length : 0
      };
    }
  }, [processedData, hasMeteredData]);

  // Update container width
  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };

    const timer = setTimeout(updateWidth, 100);
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, []);

  // InfoBox data
  const infoBoxData = useMemo(() => {
    if (!currentStats) return [];

    if (hasMeteredData) {
      return [
        { label: t('heatingChart.phase1Max'), value: `${currentStats.phase1Max.toFixed(1)} A` },
        { label: t('heatingChart.phase2Max'), value: `${currentStats.phase2Max.toFixed(1)} A` },
        { label: t('heatingChart.phase3Max'), value: `${currentStats.phase3Max.toFixed(1)} A` },
        { label: t('heatingChart.phase1Avg'), value: `${currentStats.phase1Avg.toFixed(1)} A` },
        { label: t('heatingChart.phase2Avg'), value: `${currentStats.phase2Avg.toFixed(1)} A` },
        { label: t('heatingChart.phase3Avg'), value: `${currentStats.phase3Avg.toFixed(1)} A` }
      ];
    } else {
      return [
        { label: t('heatingChart.estimatedPhaseCurrentMax'), value: `${currentStats.estimatedPhaseMax.toFixed(1)} A` },
        { label: t('heatingChart.estimatedPhaseCurrentAvg'), value: `${currentStats.estimatedPhaseAvg.toFixed(1)} A` }
      ];
    }
  }, [currentStats, hasMeteredData]);

  // Get reference line value from fuse_max (single line only)
  const referenceLine = useMemo(() => {
    if (!fuseMax) {
      console.log('PowerCurrentChart_V2: No fuse_max, reference line will be omitted');
      return null;
    }

    console.log('PowerCurrentChart_V2: Reference line at fuse_max:', fuseMax);
    return fuseMax;
  }, [fuseMax]);

  // Custom legend based on mode
  const customLegend = useMemo(() => {
    const baseLegend = hasMeteredData ? [
      { value: t('heatingChart.phase1Current'), type: 'line', color: '#DC143C' },
      { value: t('heatingChart.phase2Current'), type: 'line', color: '#FF6347' },
      { value: t('heatingChart.phase3Current'), type: 'line', color: '#FF8C69' }
    ] : [
      { value: t('heatingChart.estimatedPhaseCurrent'), type: 'line', color: '#DC143C' }
    ];

    // Add reference line to legend only if fuseMax is available
    if (referenceLine) {
      baseLegend.push(
        { value: `${referenceLine}A ` + t('heatingChart.reference'), type: 'line', color: '#ff0000', strokeDasharray: '5 5' }
      );
    }

    return baseLegend;
  }, [hasMeteredData, referenceLine]);

  // Determine Y-axis domain based on mode and fuse_max
  const yAxisDomain = useMemo(() => {
    if (fuseMax) {
      // Use auto scaling with a bit of padding above fuse_max
      return [0, Math.ceil(fuseMax * 1.15)];
    }

    // Fallback to reasonable defaults if no fuse_max
    if (hasMeteredData) {
      return [0, 40];
    } else {
      return [0, 'auto'];
    }
  }, [hasMeteredData, fuseMax]);

  return (
    <>
      <div className="center-text">
        <h3>{hasMeteredData ? t('heatingChart.phaseCurrents') : t('heatingChart.estimatedPhaseCurrent')}</h3>
      </div>

      <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={processedData}
            margin={{ top: 20, right: 60, bottom: 20, left: 20 }}
          >
            <CartesianGrid stroke="#f5f5f5" />

            <XAxis
              dataKey="timestamp"
              name="Hour"
              tickFormatter={formatHourLabel}
              ticks={hourlyTicks}
              interval={0}
              tick={{ fontSize: 12 }}
              height={40}
            />

            {/* Y-axis for Current (A) */}
            <YAxis
              label={{ value: t('heatingChart.current') + ' (A)', angle: -90, position: 'insideLeft' }}
              domain={yAxisDomain}
            />

            <Tooltip
              formatter={(value, name) => {
                return [`${Number(value).toFixed(2)} A`, name];
              }}
            />

            <Legend payload={customLegend} />

            {hasMeteredData ? (
              <>
                {/* Phase current lines */}
                <Line
                  type="monotone"
                  dataKey="phase1"
                  name={t('heatingChart.phase1Current')}
                  stroke="#DC143C"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="phase2"
                  name={t('heatingChart.phase2Current')}
                  stroke="#FF6347"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="phase3"
                  name={t('heatingChart.phase3Current')}
                  stroke="#FF8C69"
                  strokeWidth={2}
                  dot={false}
                />

                {/* Reference line for phase currents (only if fuseMax is available) */}
                {referenceLine && (
                  <ReferenceLine
                    y={referenceLine}
                    stroke="#ff0000"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{ value: `${referenceLine}A`, position: 'left' }}
                  />
                )}
              </>
            ) : (
              <>
                {/* Estimated phase current line */}
                <Line
                  type="monotone"
                  dataKey="estimatedPhaseCurrent"
                  name={t('heatingChart.estimatedPhaseCurrent')}
                  stroke="#DC143C"
                  strokeWidth={2}
                  dot={false}
                />

                {/* Reference line for estimated phase current (only if fuseMax is available) */}
                {referenceLine && (
                  <ReferenceLine
                    y={referenceLine}
                    stroke="#ff0000"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{ value: `${referenceLine}A`, position: 'left' }}
                  />
                )}
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Mobile toggle */}
      {isMobile && (
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <button onClick={() => setShowDetails(!showDetails)}>
            {showDetails ? 'Show Hourly View' : 'Show 15-min Details'}
          </button>
        </div>
      )}

      {/* InfoBox */}
      <div>
        <InfoBox
          title={hasMeteredData ? t('heatingChart.phaseCurrentStats') : t('heatingChart.totalCurrentStats')}
          dataPoints={infoBoxData}
        />
      </div>

      <p></p>
    </>
  );
};

export default PowerCurrentChart_V2;
